import { Box, CircularProgress, IconButton, MenuItem, Select, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Copy, Profile2User, Send2, TickCircle, Trash } from "iconsax-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { ConfirmDialog, CopyLinkRow, FloatingTextField, SefanoButton } from "@/components/Molecules";
import type { SettingsTeamProps } from "@/components/Templates/Settings/Settings.template.types";
import { getRoleDefinition } from "@/lib/settings/access";
import type { ShopRoleKey } from "@/lib/settings/constants";
import { formatPersianNumber } from "@/lib/format/persian";
import * as styles from "./SettingsTeamPanel.styles";

type SettingsTeamPanelProps = SettingsTeamProps;

export function SettingsTeamPanel(team: SettingsTeamPanelProps) {
  const { t } = useTranslation("settings");
  const theme = useTheme();
  const [copiedInviteId, setCopiedInviteId] = useState<number | null>(null);

  if (team.isLoading) return <CircularProgress size={28} />;

  const toneColor = (roleKey: ShopRoleKey) => theme.palette[getRoleDefinition(roleKey).tone].main;

  const copyInvite = (inviteId: number, url: string) => {
    void navigator.clipboard.writeText(url).then(() => {
      setCopiedInviteId(inviteId);
      setTimeout(() => setCopiedInviteId((current) => (current === inviteId ? null : current)), 2000);
    });
  };

  return (
    <Box sx={styles.stack}>
      {team.actionError ? (
        <Box role="alert" sx={styles.notice("error")}>
          {team.actionError}
        </Box>
      ) : null}

      {/* Members */}
      <Box component="section" sx={styles.section}>
        <Box sx={styles.sectionHeader}>
          <Typography component="h3" sx={styles.sectionTitle}>
            {t("team.membersTitle", { count: formatPersianNumber(team.members.length) })}
          </Typography>
          <Typography sx={styles.sectionHint}>{t("team.membersHint")}</Typography>
        </Box>
        {team.members.map((member) => {
          const role = getRoleDefinition(member.roleKey);
          const RoleIcon = role.icon;
          return (
            <Box key={member.userId} sx={styles.row}>
              <Box sx={styles.avatar(role.tone)}>
                <RoleIcon size={19} variant="Bold" color={toneColor(member.roleKey)} />
              </Box>
              <Box sx={styles.rowText}>
                <Typography sx={styles.rowTitle}>
                  {member.displayName}
                  {member.isYou ? ` ${t("team.youSuffix")}` : ""}
                </Typography>
                <Typography sx={styles.rowMeta} dir="ltr" textAlign="end">
                  {member.phone}
                </Typography>
              </Box>
              <Box sx={styles.rowActions}>
                {member.isOwner ? (
                  <Box component="span" sx={styles.roleChip(role.tone)}>
                    {t(role.labelKey)}
                  </Box>
                ) : (
                  <>
                    <Select
                      size="small"
                      value={member.roleKey}
                      disabled={team.isUpdatingRole}
                      onChange={(event) => team.onChangeMemberRole(member.userId, event.target.value as ShopRoleKey)}
                      sx={styles.roleSelect}
                      inputProps={{ "aria-label": t("team.changeRole") }}
                    >
                      {team.roleOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {t(option.labelKey)}
                        </MenuItem>
                      ))}
                    </Select>
                    <Tooltip title={t("team.removeMember")}>
                      <IconButton size="small" onClick={() => team.onAskRemoveMember(member)}>
                        <Trash size={18} variant="Linear" color={theme.palette.error.main} />
                      </IconButton>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
          );
        })}
        {team.members.length <= 1 ? (
          <Box sx={styles.emptyBox}>
            <Profile2User size={20} variant="Bulk" color={theme.palette.primary.main} />
            {t("team.emptyTitle")}
          </Box>
        ) : null}
      </Box>

      {/* Pending invites */}
      {team.invites.length > 0 ? (
        <Box component="section" sx={styles.section}>
          <Box sx={styles.sectionHeader}>
            <Typography component="h3" sx={styles.sectionTitle}>
              {t("team.pendingTitle", { count: formatPersianNumber(team.invites.length) })}
            </Typography>
            <Typography sx={styles.sectionHint}>{t("team.pendingHint")}</Typography>
          </Box>
          {team.invites.map((invite) => {
            const role = getRoleDefinition(invite.roleKey);
            const RoleIcon = role.icon;
            return (
              <Box key={invite.id} sx={styles.row}>
                <Box sx={styles.avatar(role.tone)}>
                  <RoleIcon size={19} variant="Linear" color={toneColor(invite.roleKey)} />
                </Box>
                <Box sx={styles.rowText}>
                  <Typography sx={styles.rowTitle} dir="ltr" textAlign="end">
                    {invite.phone}
                  </Typography>
                  {invite.isExpired ? (
                    <Typography sx={styles.expiredText}>{t("team.inviteExpired")}</Typography>
                  ) : (
                    <Typography sx={styles.rowMeta}>
                      {t("team.inviteDaysLeft", { days: formatPersianNumber(invite.daysLeft) })}
                    </Typography>
                  )}
                </Box>
                <Box sx={styles.rowActions}>
                  <Box component="span" sx={styles.roleChip(role.tone)}>
                    {t(role.labelKey)}
                  </Box>
                  {invite.isExpired ? null : (
                    <Tooltip title={copiedInviteId === invite.id ? t("team.linkCopied") : t("team.copyInviteLink")}>
                      <IconButton size="small" onClick={() => copyInvite(invite.id, invite.inviteUrl)}>
                        {copiedInviteId === invite.id ? (
                          <TickCircle size={18} variant="Bold" color={theme.palette.success.main} />
                        ) : (
                          <Copy size={18} variant="Linear" color={theme.palette.text.secondary} />
                        )}
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip title={t("team.cancelInvite")}>
                    <IconButton size="small" onClick={() => team.onAskCancelInvite(invite)}>
                      <Trash size={18} variant="Linear" color={theme.palette.error.main} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : null}

      {/* Invite */}
      <Box component="section" sx={styles.section}>
        <Box sx={styles.sectionHeader}>
          <Typography component="h3" sx={styles.sectionTitle}>
            {t("team.inviteTitle")}
          </Typography>
        </Box>
        <Typography sx={styles.sectionHint}>{t("team.selectRoleTitle")}</Typography>
        <Box sx={styles.roleGrid}>
          {team.roleCards.map((role) => {
            const RoleIcon = role.icon;
            return (
              <Box
                key={role.key}
                component="button"
                type="button"
                disabled={role.disabled}
                aria-pressed={role.selected}
                onClick={role.onSelect}
                sx={styles.roleCard(role.selected, role.disabled, role.tone)}
              >
                <RoleIcon size={20} variant={role.selected ? "Bold" : "Linear"} color={theme.palette[role.tone].main} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={styles.roleCardTitle}>{t(role.labelKey)}</Typography>
                  <Typography sx={styles.roleCardDescription}>{t(role.descriptionKey)}</Typography>
                </Box>
                {role.selected ? (
                  <TickCircle size={20} variant="Bold" color={theme.palette[role.tone].main} />
                ) : null}
              </Box>
            );
          })}
        </Box>

        <Box sx={styles.inviteRow}>
          <Box sx={{ flex: 1, width: "100%" }}>
            <FloatingTextField
              label={t("team.phone")}
              placeholder="09123456789"
              inputMode="numeric"
              value={team.invitePhone}
              error={Boolean(team.phoneError)}
              helperText={team.phoneError ? t("team.phoneInvalid") : t("team.phoneHint")}
              slotProps={{ htmlInput: { dir: "ltr", maxLength: 11 } }}
              onChange={(event) => team.onInvitePhoneChange(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && team.canSendInvite) team.onSendInvite();
              }}
            />
          </Box>
          <SefanoButton
            disabled={!team.canSendInvite}
            onClick={team.onSendInvite}
            startIcon={<Send2 size={18} variant="Linear" color="currentColor" />}
            sx={styles.inviteButton}
          >
            {team.isCreatingInvite ? t("team.sending") : t("team.sendInvite")}
          </SefanoButton>
        </Box>

        {team.inviteError ? (
          <Box role="alert" sx={styles.notice("error")}>
            {team.inviteError}
          </Box>
        ) : null}
        {team.lastInvite ? (
          <Box sx={styles.notice("success")}>
            {t("team.inviteSent", { phone: team.lastInvite.phone })}
            <CopyLinkRow url={team.lastInvite.url} size="sm" copyLabel={t("team.copyInviteLink")} />
          </Box>
        ) : null}
      </Box>

      <ConfirmDialog
        open={team.confirm !== null}
        title={team.confirm?.kind === "removeMember" ? t("team.confirmRemoveTitle") : t("team.confirmCancelTitle")}
        message={
          team.confirm?.kind === "removeMember"
            ? t("team.confirmRemoveMessage", { name: team.confirm.label })
            : t("team.confirmCancelMessage", { phone: team.confirm?.label ?? "" })
        }
        confirmLabel={team.confirm?.kind === "removeMember" ? t("team.removeMember") : t("team.cancelInvite")}
        cancelLabel={t("cancel")}
        isPending={team.isConfirmPending}
        onConfirm={team.onConfirm}
        onCancel={team.onCloseConfirm}
      />
    </Box>
  );
}
